<script lang="ts" setup>
    import { onMounted, ref, reactive, computed} from 'vue';
    import * as ReportModel from '../../lib/Models/Reports/BillerProductivityReportModel';
    import vSelect from 'vue-select'; 
    import * as _ from 'lodash';
    import { NoTzDate, getNoTzToday, getNoTzDateAdd, isValidDateRegEx } from '../../lib/DateUtils';
    import { DateDisplayFormat, ButtonDisplaySize, IconTypes, LayoutOrientation } from '../../Enums/UiEnums';
    import DatePickerNoTZ from '../../components/DatePickerNoTZ.vue';
    import { DatabaseIdTypes } from '../../Enums/ChoiceEnums';
    import ActionButtonV2 from '../../components/ActionButtonV2.vue';
    import { ActionButtonStyle } from '../../lib/Types/Types';   
    import * as ChoiceApi from '../../lib/ChoiceApi';
    import { popAlert } from '../../lib/Notification';
    import { ICol, RenderModeType } from "../../lib/ChoiceTableHelpers";
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import Icon from '../../components/Icon.vue';
    import { patientSetupScreenUrl, claimEntryScreenUrl, claimCommentScreenUrl, officeCommunicationUrl, patientCommentScreenUrl } from '../../lib/OfficeMdUrls';
    import DropDownSplitButton from '../../components/DropDownSplitButton.vue';
    import DropDownButtonItem from '../../components/DropDownButtonItem.vue';
    import RadioButtonInput from '../../components/RadioButtonInput.vue'; 

    import '../../../css/choice.css';
    import 'vue-select/dist/vue-select.css';


    const formData = reactive<{
        billerActionList: ReportModel.IBillerActionList[],
        usersAndGroupsList: ReportModel.IUserAndGroupList[],
        usersList: ReportModel.IUserAndGroupList[],
        renderingProviderList: ReportModel.ILookupList[],
        financialClassList: ReportModel.ILookupList[],
        payerList: ReportModel.IPayerList[],
        groupByDropDownList: ReportModel.IDropDownList[],
        selectedGroupBy1: string,
        selectedGroupBy2?: string,
        selectedGroupBy3?: string,
        claimCommentStartDate: NoTzDate,
        claimCommentEndDate: NoTzDate,
        completedStatusList: ReportModel.IStringList[],
        selectedBillerActionIds: number[],
        selectedCompletedStatus: string[],
        selectedAssignedTo: string[],
        selectedUser?: number[],
        selectedRenderingProvider: number[],
        selectedFinancialClass: number[],
        selectedPayer: number[],
        reportPostData?: ReportModel.IReportRequest,
        reportResponse: ReportModel.IReportResponse[],
        reportSummaryResponse: any[],
        reportNumberOfRecords: number,
        reportButtonClicked: boolean,
        displayDetailReport: boolean,
        reportDetailResponse: ReportModel.IReportResponse[],
        showReportOptions: boolean,
        currentTopItem: number,
        showComments: boolean
    }>({
        billerActionList: [],
        usersAndGroupsList: [],
        usersList: [],
        renderingProviderList: [],
        financialClassList: [],
        payerList: [],
        groupByDropDownList: 
            [{columnName: "assignedToDescription", dropDownDescription: "Assigned To"}, 
             {columnName: "billerActionDescription", dropDownDescription: "Biller Action"},
             {columnName: "date_of_service", dropDownDescription: "Claim Date of Service (Month/Year)"}, 
             {columnName: "claim_status_description", dropDownDescription: "Claim Status"}, 
             {columnName: "biller_action_completed", dropDownDescription: "Completed Status"}, 
             {columnName: "claim_primary_financial_class", dropDownDescription: "Financial Class"}, 
             {columnName: "claim_primary_payer", dropDownDescription: "Payer"}, 
             {columnName: "rendering_provider_name", dropDownDescription: "Rendering Provider"}, 
             {columnName: "comment_user_name", dropDownDescription: "User Name"}
            ],
        selectedGroupBy1: "billerActionDescription",
        claimCommentStartDate: getNoTzDateAdd(-7, DateDisplayFormat.NumericDate),
        claimCommentEndDate: getNoTzToday(),
        completedStatusList: [{dropDownValue: "Completed", dropDownDescription: "Yes"}, {dropDownValue: "Not Completed", dropDownDescription: "No"}],
        selectedBillerActionIds: [],
        selectedCompletedStatus: [],
        selectedAssignedTo: [],
        selectedUser: [],
        selectedRenderingProvider: [],
        selectedFinancialClass: [],
        selectedPayer: [],
        reportResponse: [],
        reportSummaryResponse: [],
        reportNumberOfRecords: 0,
        reportButtonClicked: false,
        displayDetailReport: false,
        reportDetailResponse: [],
        showReportOptions: true,
        currentTopItem: 0,
        showComments: false
    });

    const props = defineProps<{
        lookupBillerActions: ReportModel.IBillerActionList[],
        lookupUsersAndGroups: ReportModel.IUserAndGroupList[],
        lookupRenderingProvider: ReportModel.ILookupList[],
        lookupFinancialClass: ReportModel.ILookupList[],
        lookupPayer: ReportModel.IPayerList[]
    }>();

    const billerProdTable = ref<InstanceType<typeof ChoiceTable>>();

    onMounted(async () => {
        formData.billerActionList = _.orderBy(props.lookupBillerActions, ["description"]);

        //order our collection by groups first and add an * to the group name (users are familiar with this convention)
        formData.usersAndGroupsList = _.orderBy(_.map(props.lookupUsersAndGroups, item => {
                if (item.idType === DatabaseIdTypes.user_group_id) {
                    return { ...item, name: `*${item.name}` };
                }
                return item;
            }), ["idType"], 'desc');

        //let's map a new property to our collection that matches our values in the dw table
        formData.usersAndGroupsList = formData.usersAndGroupsList.map(item => {
            let userOrGroupId = item.idType === DatabaseIdTypes.user_group_id ? `G${item.id}` : `U${item.id}`;
            return { ...item, userOrGroupId };
            });

        const lookupAllValue = -999;
        formData.usersList = props.lookupUsersAndGroups.filter(item => item.idType === DatabaseIdTypes.user_id);  
        formData.renderingProviderList = props.lookupRenderingProvider.filter(item => +item.encode != lookupAllValue);
        formData.financialClassList = props.lookupFinancialClass.filter(item => +item.encode != lookupAllValue);
        formData.payerList = props.lookupPayer;
    });

    const runReportDisabled = computed(() => {
        return (
            !formData.claimCommentStartDate ||
            !formData.claimCommentEndDate ||
            !formData.selectedGroupBy1 ||
            !isValidDateRegEx(formData.claimCommentStartDate) ||
            !isValidDateRegEx(formData.claimCommentEndDate) 
        );
    });
    
    const showReportSummary = computed<boolean>(() => formData.reportNumberOfRecords > 0);

    interface GroupResult {
        [key: string]: any; 
    }

    function dynamicGroupByWithCountAndSort(collection: any[], GroupBy1: string, GroupBy2?: string, GroupBy3?: string): GroupResult[] {
        const grouped = _.groupBy(collection, (item) => {
            let key = item[GroupBy1];
            if (GroupBy2 && item[GroupBy2]) {
                key += `^${item[GroupBy2]}`;
            }
            if (GroupBy3 && item[GroupBy3]) {
                key += `^${item[GroupBy3]}`;
            }
            return key;
        });

        let results = _.map(grouped, (items, key) => {
            const keys = key.split('^');
            const result: GroupResult = {};

            result[GroupBy1] = keys[0];
            if (GroupBy2) result[GroupBy2] = keys[1] || null;
            if (GroupBy3) result[GroupBy3] = keys[2] || null;
            result.count = items.length;

            return result;
        });

        // Order the results by the group by's
        return _.orderBy(results, [GroupBy1, GroupBy2, GroupBy3], ['asc', 'asc', 'asc']);
    }

    function getGroupByDropDownDescription(columnName: string): string | undefined {
        const aliasInfo = formData.groupByDropDownList.find(
            (item) => item.columnName === columnName
        );
        return aliasInfo?.dropDownDescription;
    }

    function getGroupByDropDownColumnName(descName: string): string | undefined {
        const colInfo = formData.groupByDropDownList.find(
            (item) => item.dropDownDescription === descName
        );
        return colInfo?.columnName;
    }

    async function runReport() {
        formData.reportPostData = {
            commentDateStart: formData.claimCommentStartDate,
            commentDateEnd: formData.claimCommentEndDate,
            billerActionIDs: formData.selectedBillerActionIds,
            completedStatusIDs: formData.selectedCompletedStatus,
            assignedToIDs: formData.selectedAssignedTo,
            userIDs: formData.selectedUser,
            renderingProviderIDs: formData.selectedRenderingProvider,
            financialClassIDs: formData.selectedFinancialClass,
            payerIDs: formData.selectedPayer
        }

        const resp = await ChoiceApi.reporting.billerProductivity.runBillerProductivityReport(formData.reportPostData);

        if (!resp) {
            await popAlert("Error pulling biller productivity report data", "Report Error"); 
        }
        else {
            formData.reportResponse = resp;
            formData.reportNumberOfRecords = resp.length;
            formData.reportButtonClicked = true;
            formData.displayDetailReport = false;
            formData.showReportOptions = !formData.reportNumberOfRecords; 
            
            let groupedWithCountAndSort = dynamicGroupByWithCountAndSort(formData.reportResponse, formData.selectedGroupBy1, formData.selectedGroupBy2, formData.selectedGroupBy3);
            formData.reportSummaryResponse = groupedWithCountAndSort;
        }
    }

    function getDetailData(collection: any[], property1: string, value1: any, property2?: string, value2?: any, property3?: string, value3?: any) {
        return collection.filter((item) => {
            if (item[property1] !== value1) {
                return false; //Group1 doesn't match, no need to check further
            }

            if (undefined !== property2 && item[property2] !== value2) {
                return false; //Group2 is defined and doesn't match
            }

            if (undefined !== property3 && item[property3] !== value3) {
                return false; //Group3 is defined and doesn't match
            }

            return true; //All groups match
        });
    }

    async function showDetailReport(itemClicked: any) {
        const matchingItems = getDetailData(formData.reportResponse, formData.selectedGroupBy1, itemClicked[formData.selectedGroupBy1], 
            formData.selectedGroupBy2, undefined !== formData.selectedGroupBy2 ? itemClicked[formData.selectedGroupBy2] : undefined, 
            formData.selectedGroupBy3, undefined !== formData.selectedGroupBy3 ? itemClicked[formData.selectedGroupBy3] : undefined);

        formData.reportDetailResponse = `total` === itemClicked ? formData.reportResponse : matchingItems;   
        formData.displayDetailReport = true;         
    }

    const tableColumns = computed(() => {
        const baseColumns: ICol[] = [
            { Name: 'Actions', DataField: 'empty' },
            { Name: 'Claim Status', DataField: 'claim_status_description' },
            { Name: 'Last Note Date', DataField: 'last_note_date', RenderMode: RenderModeType.PrettyDate }, 
            { Name: 'Follow-up Date', DataField: 'followup_date', RenderMode: RenderModeType.PrettyDate },
            { Name: 'Assigned To', DataField: 'assignedToDescription' },
            { Name: 'Completed Status', DataField: 'biller_action_completed' },
            { Name: 'Claim Level', DataField: 'claim_level' },
            { Name: 'Claim ID', DataField: 'claim_id' },
            { Name: 'DOS', DataField: 'date_of_service', RenderMode: RenderModeType.PrettyDate },
            { Name: 'Last Submission Date', DataField: 'last_submission_date', RenderMode: RenderModeType.PrettyDate },
            { Name: 'Account', DataField: 'patient_id' },
            { Name: 'Patient', DataField: 'patient_name' },
            { Name: 'Primary Payer', DataField: 'claim_primary_payer' },
            { Name: 'Secondary Payer', DataField: 'claim_secondary_payer' },
            { Name: 'Tertiary Payer', DataField: 'claim_tertiary_payer' },
            { Name: 'Charges', DataField: 'claim_charges', RenderMode: RenderModeType.AccountingMoney },
            { Name: 'Balance', DataField: 'claim_balance', RenderMode: RenderModeType.AccountingMoney },
        ];

        if (formData.showComments) {
            baseColumns.push({ Name: 'Comment', DataField: 'comment_text' });
        }

        return baseColumns;
    });


    async function updateReport() {
        if (showReportSummary.value) {
            runReport();
        }
    }

    function openClaim(row: ReportModel.IReportResponse) {
        window.open(claimEntryScreenUrl(row.claim_id), "_blank", "resizable=true,width=1050,height=900");
    }

    function openPatient(row: ReportModel.IReportResponse) {
        window.open(patientSetupScreenUrl(DatabaseIdTypes.patient_id, row.patient_id ?? 0), "_blank", "resizable=true,width=1050,height=900");
    }

    function openClaimComment(row: any) {
        window.open(claimCommentScreenUrl(row.claim_id), "_blank", "resizable=true,width=1050,height=700");
    }

    async function openPatientComment(row: any) {
        //get patient entity id using the patient id
        const entityData = await ChoiceApi.entityInfo.getEntityInfoByDatabaseId({ id: row.patient_id, idType: DatabaseIdTypes.patient_id });
        window.open(patientCommentScreenUrl(entityData.entityId), "_blank", "resizable=true,width=1050,height=700");
    }

    function openOfficeCommunication(row: any, includeClaim: boolean) {
        if (includeClaim) {
            window.open(officeCommunicationUrl(row.patient_id, row.claim_id), "_blank", "resizable=true,width=900,height=600");
        }  
        else {
            window.open(officeCommunicationUrl(row.patient_id), "_blank", "resizable=true,width=900,height=600");
        }      
    }


    async function toggleReportOptionsdisplay() {
        formData.showReportOptions = !formData.showReportOptions; 
    }

    function exportChoiceTableToExcel() {
        billerProdTable.value?.exportToExcel("BillerProductivityDetail.xls", "BillerProductivity");
    }

</script>


<template>
    <div class="isalus-screen-header text-center">Biller Productivity Report</div>
    <hr>

    <div class="pb-2">
        <ActionButtonV2
            v-if="formData.showReportOptions"
            :action="ActionButtonStyle.NoIcon"
            :disabled="runReportDisabled"
            :text="`Run Report`"
            :spinText="`Running Report...`"
            :size="ButtonDisplaySize.Small" 
            :performAction="runReport" 
            :endingPadding="2"/>
        <ActionButtonV2
            v-if="formData.showReportOptions"
            :action="ActionButtonStyle.ChveronUp"
            :text="`Hide Report Filters`"
            :size="ButtonDisplaySize.Small" 
            :performAction="toggleReportOptionsdisplay" />
        <ActionButtonV2
            v-if="!formData.showReportOptions"
            :action="ActionButtonStyle.ChveronDown"
            :text="`Show Report Filters`"
            :size="ButtonDisplaySize.Small" 
            :performAction="toggleReportOptionsdisplay" />
    </div>

    <!----------------------------------------------------------------------------------------------------------------------------------------------->
    <!-- Report Selection Criteria Section -->
    <!----------------------------------------------------------------------------------------------------------------------------------------------->
    <div v-if="formData.showReportOptions">
        <div class="row ps-3 gray-text fw-bold">Group and Display Options</div>
        <div class="container-fluid label-isalus-medium" style="font-size: 0.75rem">        
            <div class="row">
                <div class="col col-sm-1 border-isalus-primary background-isalus-gray fw-bold pt-1" title="Claim Counts will be grouped by this first">Group 1:</div>
                <div class="col border-isalus-primary pt-1 pb-1">
                    <v-select                     
                        style="font-size: 0.75rem"
                        v-model="formData.selectedGroupBy1"
                        :options="formData.groupByDropDownList" 
                        @option:selected="updateReport"
                        @option:deselected="updateReport"
                        :reduce="item => item.columnName" label="dropDownDescription" />
                        <div v-if="!formData.selectedGroupBy1" class="red-text mt-0 mb-1">Required</div>  
                </div>                 
                <div class="col col-sm-1 border-isalus-primary background-isalus-gray fw-bold pt-1" title="Claim Counts will be grouped by this second">Group 2:</div>
                <div class="col border-isalus-primary pt-1 pb-1">
                    <v-select 
                        style="font-size: 0.75rem"
                        v-model="formData.selectedGroupBy2"
                        :options="formData.groupByDropDownList" 
                        @option:selected="updateReport"
                        @option:deselected="updateReport"
                        :reduce="item => item.columnName" label="dropDownDescription" />
                </div>
                <div class="col col-sm-1 border-isalus-primary background-isalus-gray fw-bold pt-1" title="Claim Counts will be grouped by this third">Group 3:</div>
                <div class="col border-isalus-primary pt-1 pb-1">
                    <v-select 
                        style="font-size: 0.75rem"
                        v-model="formData.selectedGroupBy3"
                        :options="formData.groupByDropDownList" 
                        @option:selected="updateReport"
                        @option:deselected="updateReport"
                        :reduce="item => item.columnName" label="dropDownDescription" />
                </div>
            </div>
        </div>

        <div class="row ps-3 gray-text fw-bold mt-3">Filter Options</div>
        <div class="container-fluid label-isalus-medium" style="font-size: 0.75rem">        
            <div class="row">
                <div class="col col-sm-2 border-isalus-primary background-isalus-gray fw-bold pt-1">*Claim Comment Start Date:</div>
                <div class="col border-isalus-primary pt-1 pb-1">
                    <DatePickerNoTZ v-model:value="formData.claimCommentStartDate" :clearable="false" :required="true" :display-format="DateDisplayFormat.NumericDate"/>
                </div>
                <div class="col col-sm-2 border-isalus-primary background-isalus-gray fw-bold pt-1">Biller Actions:</div>
                <div class="col border-isalus-primary pt-1 pb-1">
                    <v-select 
                        style="font-size: 0.75rem"
                        v-model="formData.selectedBillerActionIds"
                        :options="formData.billerActionList" 
                        :reduce="item => item.biller_action_id" label="description" 
                        :multiple="true" 
                        :searchable="true"
                        :filterable="true"
                        :closeOnSelect="false"
                        :select-on-tab="true" />
                </div>
            </div>
            <div class="row">
                <div class="col col-sm-2 border-isalus-primary background-isalus-gray fw-bold pt-1">*Claim Comment End Date:</div>
                <div class="col border-isalus-primary pt-1 pb-1">
                    <DatePickerNoTZ v-model:value="formData.claimCommentEndDate" :clearable="false" :required="true" :display-format="DateDisplayFormat.NumericDate"/>
                </div>
                <div class="col col-sm-2 border-isalus-primary background-isalus-gray fw-bold pt-1">Completed Status:</div>
                <div class="col border-isalus-primary pt-1 pb-1">
                    <v-select 
                        style="font-size: 0.75rem"
                        v-model="formData.selectedCompletedStatus"
                        :options="formData.completedStatusList" 
                        :reduce="item => item.dropDownValue" label="dropDownDescription" 
                        :multiple="true" 
                        :searchable="true"
                        :filterable="true"
                        :closeOnSelect="false"
                        :select-on-tab="true" />
                </div>
            </div>
            <div class="row">
                <div class="col col-sm-2 border-isalus-primary background-isalus-gray fw-bold pt-1" title="The user or group that a biller action was assigned to">Assigned To:</div>
                <div class="col border-isalus-primary pt-1 pb-1">
                    <v-select 
                        style="font-size: 0.75rem"
                        v-model="formData.selectedAssignedTo"
                        :options="formData.usersAndGroupsList" 
                        :reduce="item => item.userOrGroupId" label="name" 
                        :multiple="true" 
                        :searchable="true"
                        :filterable="true"
                        :closeOnSelect="false"
                        :select-on-tab="true">
                            <template v-slot:option="option">
                                <span v-if="DatabaseIdTypes.user_id === option.idType"><Icon :icon="IconTypes.User"></Icon></span>
                                <span v-if="DatabaseIdTypes.user_group_id === option.idType"><Icon :icon="IconTypes.Users"></Icon></span>
                                {{ option.name }}
                            </template>
                            <template #selected-option="{ idType, name }">
                                <div>
                                    <span v-if="DatabaseIdTypes.user_id === idType"><Icon :icon="IconTypes.User"></Icon></span>
                                    <span v-if="DatabaseIdTypes.user_group_id === idType"><Icon :icon="IconTypes.Users"></Icon></span>
                                    <span class="ps-1">{{ name }}</span>
                                </div>
                            </template>
                    </v-select>
                </div>
                <div class="col col-sm-2 border-isalus-primary background-isalus-gray fw-bold pt-1" title="Comment User">User:</div>
                <div class="col border-isalus-primary pt-1 pb-1">
                    <v-select 
                        style="font-size: 0.75rem"
                        v-model="formData.selectedUser"
                        :options="formData.usersList" 
                        :reduce="item => item.id" label="name" 
                        :multiple="true" 
                        :searchable="true"
                        :filterable="true"
                        :closeOnSelect="false"
                        :select-on-tab="true" />
                </div>
            </div>
            <div class="row">
                <div class="col col-sm-2 border-isalus-primary background-isalus-gray fw-bold pt-1" title="Rendering provider on the claim">Rendering Provider:</div>
                <div class="col border-isalus-primary pt-1 pb-1">
                    <v-select 
                        style="font-size: 0.75rem"
                        v-model="formData.selectedRenderingProvider"
                        :options="formData.renderingProviderList" 
                        :reduce="item => +item.encode" label="decode" 
                        :multiple="true" 
                        :searchable="true"
                        :filterable="true"
                        :closeOnSelect="false"
                        :select-on-tab="true" />
                </div>
                <div class="col col-sm-2 border-isalus-primary background-isalus-gray fw-bold pt-1">Financial Class:</div>
                <div class="col border-isalus-primary pt-1 pb-1">
                    <v-select 
                        style="font-size: 0.75rem"
                        v-model="formData.selectedFinancialClass"
                        :options="formData.financialClassList" 
                        :reduce="item => +item.encode" label="decode" 
                        :multiple="true" 
                        :searchable="true"
                        :filterable="true"
                        :closeOnSelect="false"
                        :select-on-tab="true" />
                </div>
            </div>
            <div class="row">
                <div class="col col-sm-2 border-isalus-primary background-isalus-gray fw-bold pt-1">Payer:</div>
                <div class="col border-isalus-primary pt-1 pb-1">
                    <v-select 
                        style="font-size: 0.75rem"
                        v-model="formData.selectedPayer"
                        :options="formData.payerList" 
                        :reduce="item => item.entity_id" label="PayerName" 
                        :multiple="true" 
                        :searchable="true"
                        :filterable="true"
                        :closeOnSelect="false"
                        :select-on-tab="true" />
                </div>
                <div class="col col-sm-2 border-isalus-primary background-isalus-gray fw-bold pt-1">Include Comment:</div>
                <div class="col border-isalus-primary pt-1 pb-1">  
                    <RadioButtonInput 
                        :value="true" 
                        v-model="formData.showComments" 
                        :layout="LayoutOrientation.Horz">  
                        <div class="label-isalus-small">Yes</div>
                    </RadioButtonInput>
                    <RadioButtonInput 
                        :value="false" 
                        v-model="formData.showComments" 
                        :layout="LayoutOrientation.Horz">  
                        <div class="label-isalus-small">No</div>
                    </RadioButtonInput>              
                </div>
            </div>
        </div>
    </div>

    <!----------------------------------------------------------------------------------------------------------------------------------------------->
    <!-- Summary Report Section -->
    <!----------------------------------------------------------------------------------------------------------------------------------------------->
    <div v-if="formData.reportButtonClicked" class="container-fluid pt-3"> 
        <div v-if="!showReportSummary" class="text-center fs-6"> 
            No records found matching your report criteria
        </div>
        <div v-if="showReportSummary">
            <table class="table table-hover" style="font-size: 0.75rem" aria-describedby="Biller Productivity Summary">
                <tr class="background-isalus-primary-light">
                    <th class="border-isalus-primary ps-1">
                        {{ getGroupByDropDownDescription(formData.selectedGroupBy1) }}
                    </th>
                    <th class="border-isalus-primary ps-1" v-if="formData.selectedGroupBy2">
                        {{ getGroupByDropDownDescription(formData.selectedGroupBy2) }}
                    </th>
                    <th class="border-isalus-primary ps-1" v-if="formData.selectedGroupBy3">
                        {{ getGroupByDropDownDescription(formData.selectedGroupBy3) }}
                    </th>
                    <th class="border-isalus-primary text-end">
                        Count
                    </th>
                </tr>
                <tbody style="font-size: 0.75rem">
                    <tr v-for="item in formData.reportSummaryResponse" style="cursor: pointer;" title="Click to view detail of this row" @click="showDetailReport(item)">
                        <td class="border-isalus-primary">
                            {{ item[formData.selectedGroupBy1] }}
                        </td>
                        <td class="border-isalus-primary" v-if="formData.selectedGroupBy2">
                            {{ item[formData.selectedGroupBy2] }}
                        </td>
                        <td class="border-isalus-primary" v-if="formData.selectedGroupBy3">
                            {{ item[formData.selectedGroupBy3] }}
                        </td>
                        <td class="border-isalus-primary text-end">
                            {{ item.count }}
                        </td>
                    </tr>
                    <tr class="background-isalus-primary-light" title="Click to view detail of total" @click="showDetailReport('total')">
                        <td class="border-isalus-primary fw-bold">
                            Total
                        </td>
                        <td class="border-isalus-primary" v-if="formData.selectedGroupBy2">
                            
                        </td>
                        <td class="border-isalus-primary" v-if="formData.selectedGroupBy3">
                            
                        </td>
                        <td class="border-isalus-primary fw-bold text-end">
                            {{ formData.reportNumberOfRecords }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <!----------------------------------------------------------------------------------------------------------------------------------------------->
    <!-- Detail Report Section -->
    <!----------------------------------------------------------------------------------------------------------------------------------------------->
    <div v-if="formData.displayDetailReport" class="container-fluid pt-3"> 
        <ActionButtonV2
            :action="ActionButtonStyle.Excel"
            :text="`Export to Excel`"
            :spinText="`Exporting...`"
            :size="ButtonDisplaySize.Small" 
            :performAction="exportChoiceTableToExcel" />
        <div class="row pt-1">
            <ChoiceTable 
                ref="billerProdTable"
                :columns="tableColumns" 
                :data="formData.reportDetailResponse" 
                :show-filters="false" 
                :allowSorting="true"
                fontSize="smaller"
                :itemsPerPage="20"                
                :showPaginationButtonsBelowChoiceTable="true"
                :key-value-data-member="'report_id'"
            >
                <template #cell="{col, row, value}">
                    <template v-if="'Actions' === col.Name">
                        <DropDownSplitButton :baseClass="'btn btn-primary label-isalus-small'">
                            <template v-slot:button><i class="fa-solid fa-gear"></i></template>
                            <DropDownButtonItem :baseClass="'dropdown-item label-isalus-small'" @click="openClaim(row as any)">
                                Open the claim
                            </DropDownButtonItem>
                            <DropDownButtonItem :baseClass="'dropdown-item label-isalus-small'" @click="openClaimComment(row as any)">
                                Open the claim comment
                            </DropDownButtonItem>
                            <DropDownButtonItem :baseClass="'dropdown-item label-isalus-small'" @click="openOfficeCommunication(row as any, true)">
                                Open the claim communication
                            </DropDownButtonItem>
                            <DropDownButtonItem :baseClass="'dropdown-item label-isalus-small'" @click="openPatient(row as any)">
                                Open the patient
                            </DropDownButtonItem>
                            <DropDownButtonItem :baseClass="'dropdown-item label-isalus-small'" @click="openPatientComment(row as any)">
                                Open the patient comment
                            </DropDownButtonItem>
                            <DropDownButtonItem :baseClass="'dropdown-item label-isalus-small'" @click="openOfficeCommunication(row as any, false)">
                                Open the patient communication
                            </DropDownButtonItem>
                        </DropDownSplitButton>
                    </template>
                    <template v-if="undefined != row.assignedToId && 'Assigned To' === col.Name && row.assignedToId.charAt(0) === 'U'">
                        <Icon v-if="value.trim().length > 0" :icon="IconTypes.User"></Icon>
                            {{ value }}
                    </template>
                    <template v-if="undefined != row.assignedToId && 'Assigned To' === col.Name && row.assignedToId.charAt(0) === 'G'">
                        <Icon v-if="value.trim().length > 0" :icon="IconTypes.Users"></Icon>
                            {{ value }}
                    </template>
                    <template v-if="'Comment' === col.Name  && value.length > 50">
                        <span :title="value">{{ value.substring(0, 50) }}...</span>
                    </template>
                    <template v-if="'Comment' === col.Name  && value.length <= 50">
                        <span :title="value">{{ value }}</span>
                    </template>                    
                </template>
            </ChoiceTable>            
        </div>
    </div>
</template>
